// PRO Dashboard Types
// Aligned with Sessions + SessionMetrics database schema

export interface ProEntitlement {
  hasPro: boolean;
  plan: string | null;
  expiresAt: string | null;
  source: string | null;
  userId: string;
  email: string;
}

export interface SessionSummary {
  sessionKey: string;
  vin: string | null;
  vehicleYear: string | null;  // nvarchar(10) in DB
  vehicleMake: string | null;
  vehicleModel: string | null;
  createdAt: string;
  updatedAt: string | null;
  dtcCount: number;
  detectedDTCs: string | null;  // Comma-separated DTC codes
  readyMonitorsCount: number | null;
  notReadyMonitorsCount: number | null;
  milStatus: boolean | null;
  hasReport: boolean;
}

export interface SessionsResponse {
  sessions: SessionSummary[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface DTCInfo {
  code: string;
  description: string | null;
  status: string | null;
  severity: string | null;
}

export interface MonitorInfo {
  monitorName: string;
  status: string;
}

export interface ChatMessage {
  role: string;
  content: string;
  modelName: string;
  timestamp: string;
  responseTimeMs: number | null;
}

export interface ReportInfo {
  reportId: string;
  reportTitle: string;
  generatedAt: string;
  fileSizeBytes: number | null;
}

export interface SessionDetail {
  sessionKey: string;
  vin: string | null;
  vehicleYear: string | null;
  vehicleMake: string | null;
  vehicleModel: string | null;
  createdAt: string;
  updatedAt: string | null;
  dtcCount: number;
  milStatus: boolean | null;
  readyMonitorsCount: number | null;
  notReadyMonitorsCount: number | null;
  interfaceType: string | null;
  interfaceName: string | null;
  dtcs: DTCInfo[];
  monitors: MonitorInfo[];
  report: ReportInfo | null;
  chatHistory: ChatMessage[];
}

export interface VehicleSummary {
  vin: string;
  vehicleYear: string | null;
  vehicleMake: string | null;
  vehicleModel: string | null;
  sessionCount: number;
  lastScanDate: string;
  firstScanDate: string;
  totalDTCsFound: number;
  reportCount: number;
}

export interface VehiclesResponse {
  vehicles: VehicleSummary[];
}

export interface SessionTrendData {
  sessionKey: string;
  createdAt: string;
  dtcCount: number;
  readyMonitors: number;
  totalMonitors: number;
  milStatus: boolean | null;
}

export interface DTCHistoryItem {
  code: string;
  description: string | null;
  occurredAt: string;
}

export interface VehicleDetail {
  vin: string;
  vehicleYear: string | null;
  vehicleMake: string | null;
  vehicleModel: string | null;
  sessionHistory: SessionTrendData[];
  dtcHistory: DTCHistoryItem[];
}

export interface ReportListItem {
  reportId: string;
  sessionKey: string;
  vin: string | null;
  vehicleYear: string | null;
  vehicleMake: string | null;
  vehicleModel: string | null;
  reportTitle: string;
  reportType: string | null;
  generatedAt: string;
  fileSizeBytes: number | null;
  shopName: string | null;
}

export interface ReportsResponse {
  reports: ReportListItem[];
}

export interface ReportDownload {
  downloadUrl: string;
  expiresAt: string;
  fileName: string;
}

export interface VehicleSummaryResponse {
  vin: string;
  summary: string;
  totalSessions: number;
  lastScan: string | null;
  generatedAt: string;
}

export interface InjectedRepairSelection {
  title: string;
  description?: string;
  priceSource: 'independent' | 'dealer' | 'custom';
  customTotal?: number;
  customParts?: number;
  customLabor?: number;
  dbIndependentAvg?: number;
  dbDealerAvg?: number;
}

export interface InjectedVehicleData {
  mileage?: number;
  warranty?: Record<string, string>;
  maintenance?: Array<{
    miles: number;
    km?: number;
    serviceItems: string[];
  }>;
  repairs?: InjectedRepairSelection[];
  marketValue?: any;
}

export interface GenerateReportRequest {
  sessionKey: string;
  reportType: 'emissions' | 'diagnostic' | 'pre_purchase' | 'estimate';
  customInstructions?: string;
  injectedData?: InjectedVehicleData;
  clientTimezone?: string;
}

export interface GenerateReportResponse {
  markdown: string;
  reportType: string;
  sessionKey: string;
  modelUsed: string;
}

export interface RefineReportRequest {
  sessionKey: string;
  currentMarkdown: string;
  instructions: string;
}

export interface RefineReportResponse {
  markdown: string;
  modelUsed: string;
}

export interface FinalizeReportRequest {
  sessionKey: string;
  vin: string;
  vehicleYear: string;
  vehicleMake: string;
  vehicleModel: string;
  reportType: string;
  markdown: string;
  clientTimezone?: string;
}

export interface ShopProfile {
  profileId: string | null;
  shopName: string | null;
  shopPhone: string | null;
  shopEmail: string | null;
  shopAddress: string | null;
  logoBlobPath: string | null;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface ShopProfileUpdate {
  shopName: string | null;
  shopPhone: string | null;
  shopEmail: string | null;
  shopAddress: string | null;
  logoBase64: string | null;
}

export interface LogoUrlResponse {
  logoUrl: string | null;
  expiresAt: string | null;
}

// Helper function to format VIN — guards against .NET serialization artifacts
export function formatVin(vin: string | null | undefined): string {
  if (!vin || vin.startsWith('System.') || vin.length < 5) {
    return 'NO VIN';
  }
  return vin;
}

// Helper function to format vehicle name
export function formatVehicle(
  year: string | number | null | undefined,
  make: string | null | undefined,
  model: string | null | undefined
): string {
  const parts = [year, make, model].filter(Boolean);
  return parts.length > 0 ? parts.join(' ') : 'Unknown Vehicle';
}

// Helper function to format file size
export function formatFileSize(bytes: number | null | undefined): string {
  if (!bytes) return 'Unknown';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Helper to parse DTC codes from comma-separated string
export function parseDTCCodes(detectedDTCs: string | null | undefined): string[] {
  if (!detectedDTCs) return [];
  return detectedDTCs.split(',').map(code => code.trim()).filter(Boolean);
}

// Helper to calculate monitor readiness percentage
export function getMonitorReadinessPercent(
  ready: number | null | undefined,
  notReady: number | null | undefined
): number {
  const readyCount = ready ?? 0;
  const notReadyCount = notReady ?? 0;
  const total = readyCount + notReadyCount;
  if (total === 0) return 0;
  return Math.round((readyCount / total) * 100);
}

// Helper to parse UTC timestamps from the API into local Date objects.
// The backend stores all dates in UTC but serializes DateTime without a 'Z' suffix,
// so JavaScript's new Date() misinterprets them as local time. This ensures UTC parsing.
export function utcToLocal(dateStr: string | null | undefined): Date {
  if (!dateStr) return new Date(NaN);
  const s = dateStr.trim();
  // Already has timezone info — parse as-is
  if (s.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(s)) return new Date(s);
  // Append Z to treat as UTC
  return new Date(s + 'Z');
}

// Helper to format session duration
export function formatDuration(
  startDate: string | null | undefined,
  endDate: string | null | undefined
): string {
  if (!startDate || !endDate) return '-';
  const start = utcToLocal(startDate).getTime();
  const end = utcToLocal(endDate).getTime();
  const diffMs = end - start;
  if (diffMs < 0) return '-';

  const totalSeconds = Math.floor(diffMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return minutes > 0 ? `${hours} hr ${minutes} min` : `${hours} hr`;
  }
  if (minutes > 0) {
    return `${minutes} min`;
  }
  return `${seconds} sec`;
}

// ============================================
// Rich Session Data Types (from CommLogProcessor)
// ============================================

export interface PidStatistics {
  description: string;
  min_value: number;
  max_value: number;
  avg_value: number;
  std_dev: number;
  sample_count: number;
  units: string;
}

export interface MonitorState {
  supported: boolean;
  complete: boolean;
  value?: number;
}

export interface RichDtcInfo {
  code: string;
  description: string;
  severity: string;
}

// OBDonUDS (SAE J1979-2) $19 $06 extended data record for one DTC:
// 0x90 B1 counter, 0x91 DTC-based IUMPR, 0x92 test result vs limits, 0x93 monitor activity.
export interface DtcExtendedRecord {
  dtc: string;          // "P0196"
  full_code: string;    // "P0196-01" (J2012 code + failure type byte)
  record: string;       // "0x91"
  name: string;
  fields: Record<string, unknown>;
  raw_hex?: string;
}

export interface DtcData {
  stored_dtcs: RichDtcInfo[];
  pending_dtcs: RichDtcInfo[];
  permanent_dtcs: RichDtcInfo[];
  has_emissions_dtcs: boolean;
  extended_records?: DtcExtendedRecord[];   // OBDonUDS sessions only
}

// OBDonUDS: the ECU's own ISO 14229 status bits for a DTC, as reported in its readiness group.
export interface ReadinessGroupDtc {
  code: string;
  full_code: string;
  description?: string;
  status: string;                 // "0xAF"
  complete_since_clear: boolean;
  pending: boolean;
  confirmed: boolean;
  test_failed: boolean;
  mil_requested: boolean;
}

export interface ReadinessGroup {
  rgid: string;
  name: string;
  complete: boolean;
  dtcs: ReadinessGroupDtc[];
}

export interface VehicleState {
  readiness_groups?: Record<string, ReadinessGroup>;   // OBDonUDS sessions only
  readiness: Record<string, MonitorState>;
  overall_readiness: boolean;
}

export interface FreezeFrameAIPidValue {
  description: string;
  value: number | string;
  units?: string;
}

export interface FreezeFrameAISnapshot {
  frame_number: number;
  triggering_dtc: string;
  triggering_dtc_description?: string;
  sensor_data: Record<string, FreezeFrameAIPidValue>;
}

export interface FreezeFrameAIData {
  frames: FreezeFrameAISnapshot[];
}

export interface VehicleAnalysisSession {
  session_id: string;
  vin_mask: string;
  test_time: string;
  mil_status: boolean;
  vehicle_state: VehicleState;
  dtc_data: DtcData;
  subsystems: Record<string, unknown>;
  pid_statistics: Record<string, PidStatistics>;
  supported_pids: string[];  // Array of hex codes like ["0x05", "0x0B", ...]
  supported_pid_descriptions?: Record<string, string>;  // Optional: {hex: description} for Pro Dashboard
  freeze_frame?: FreezeFrameAIData;  // Mode 02 snapshots captured when DTCs set
}

export interface CommLogMetadata {
  vin: string;
  session_count: number;
  date_range: {
    start: string;
    end: string;
  };
  mil_status: {
    on_count: number;
    off_count: number;
  };
}

export interface CommLogJsonResponse {
  metadata: CommLogMetadata;
  sessions: VehicleAnalysisSession[];
}

// Second-by-second data types
export interface SecondBySecondReading {
  pid: string;
  value: number;
  units: string;
}

export interface SecondBySecondRecord {
  vin: string;
  session_id: string;
  timestamp: string;
  data: SecondBySecondReading[];
}

// Second-by-second data point
export interface SecondBySecondReading {
  timestamp: string;
  pid: string;
  description: string;
  value: number;
  units: string;
}

// ============================================
// VehicleDatabases API Response Types
// ============================================

// NHTSA Free VIN Decode (auto-called on session detail)
// Complete NHTSA vPIC VIN Decode Result - All 144 Vehicle Variables
export interface NhtsaVinDecodeResult {
  // Core Vehicle Identification
  vin: string;
  year: number | null;
  make: string | null;
  model: string | null;
  trim: string | null;
  series: string | null;
  series2: string | null;

  // Manufacturer Information
  manufacturer: string | null;
  manufacturerId: string | null;
  plantCity: string | null;
  plantState: string | null;
  plantCountry: string | null;
  plantCompanyName: string | null;

  // Vehicle Type & Class
  vehicleType: string | null;
  bodyClass: string | null;
  doors: string | null;
  windows: string | null;
  wheelBaseType: string | null;
  trackWidth: string | null;
  gvwr: string | null;
  gvwrFrom: string | null;
  gvwrTo: string | null;
  gcwr: string | null;
  gcwrFrom: string | null;
  gcwrTo: string | null;
  curbWeightLb: string | null;
  wheelBaseLong: string | null;
  wheelBaseShort: string | null;
  bedLength: string | null;
  bedType: string | null;
  cabType: string | null;
  trailerType: string | null;
  trailerBodyType: string | null;
  trailerLength: string | null;
  busFloorConfigType: string | null;
  busLength: string | null;
  busType: string | null;
  customMotorcycleType: string | null;
  motorcycleSuspensionType: string | null;
  motorcycleChassisType: string | null;
  otherMotorcycleInfo: string | null;

  // Engine Information
  engineConfiguration: string | null;
  engineCylinders: string | null;
  displacementCc: string | null;
  displacementCi: string | null;
  displacementL: string | null;
  engineModel: string | null;
  engineManufacturer: string | null;
  engineHp: string | null;
  engineHpFrom: string | null;
  engineHpTo: string | null;
  engineKw: string | null;
  engineBrakeBtoh: string | null;
  engineBrakeBfrom: string | null;
  engineBrakeBto: string | null;
  coolingType: string | null;
  turbo: string | null;
  topSpeedMph: string | null;
  valveTrainDesign: string | null;

  // Fuel & Emissions
  fuelTypePrimary: string | null;
  fuelTypeSecondary: string | null;
  fuelInjectionType: string | null;
  otherEngineInfo: string | null;

  // Transmission & Drive
  transmissionStyle: string | null;
  transmissionSpeeds: string | null;
  driveType: string | null;
  axles: string | null;
  axleConfiguration: string | null;

  // Brakes & Suspension
  brakeSystemType: string | null;
  brakeSystemDesc: string | null;

  // Steering
  steeringLocation: string | null;

  // Electric Vehicle Info
  electrificationLevel: string | null;
  evDriveUnit: string | null;
  batteryInfo: string | null;
  batteryCells: string | null;
  batteryModules: string | null;
  batteryPacks: string | null;
  batteryType: string | null;
  batteryKwh: string | null;
  batteryKwhFrom: string | null;
  batteryKwhTo: string | null;
  batteryV: string | null;
  batteryVFrom: string | null;
  batteryVTo: string | null;
  chargerLevel: string | null;
  chargerPowerKw: string | null;

  // Safety Features - Crash Avoidance
  abs: string | null;
  esc: string | null;
  traction: string | null;
  adaptiveCruiseControl: string | null;
  adaptiveDrivingBeam: string | null;
  adaptiveHeadlights: string | null;
  autoReverseSystem: string | null;
  automaticPedestrianAlertingSound: string | null;
  blindSpotMon: string | null;
  blindSpotIntervention: string | null;
  crashImminentBraking: string | null;
  forwardCollisionWarning: string | null;
  dynamicBrakeSupport: string | null;
  laneCenteringAssistance: string | null;
  laneDepartureWarning: string | null;
  laneKeepSystem: string | null;
  parkAssist: string | null;
  pedestrianAutomaticEmergencyBraking: string | null;
  rearAutomaticEmergencyBraking: string | null;
  rearCrossTrafficAlert: string | null;
  rearVisibilitySystem: string | null;
  semiautomaticHeadlampBeamSwitching: string | null;

  // Safety Features - Airbags
  airBagLocFront: string | null;
  airBagLocSide: string | null;
  airBagLocCurtain: string | null;
  airBagLocSeatCushion: string | null;
  airBagLocKnee: string | null;
  seatBeltsAll: string | null;
  seatRows: string | null;
  seats: string | null;

  // Safety Features - Other
  daytimeRunningLight: string | null;
  keyless: string | null;
  destinationMarket: string | null;
  tpms: string | null;
  activeSafetySysNote: string | null;

  // NCSA Information
  ncsaBodyType: string | null;
  ncsaMake: string | null;
  ncsaModel: string | null;
  ncsaNote: string | null;

  // Additional Info
  note: string | null;
  basePrice: string | null;
  possibleValues: string | null;
  suggestedVin: string | null;
  errorCode: string | null;
  errorText: string | null;
  vehicleDescriptor: string | null;
}

// Paid VehicleDatabases.com VIN Decode (premium)
export interface VinDecodeResult {
  status: string;
  data?: {
    vin: string;
    year: number;
    make: string;
    model: string;
    trim: string;
    engine: string;
    transmission: string;
    drivetrain: string;
    fuel_type: string;
    body_type: string;
    doors: number;
    [key: string]: unknown;
  };
  error?: string;
}

// VehicleDatabases.com API Response Types (based on actual responses)

// Market Value - actual response structure
export interface MarketValueData {
  status: string;
  data?: {
    basic?: {
      make?: string;
      model?: string;
      year?: string;
      trim?: string;
      state?: string;
      mileage?: string;
    };
    market_value?: {
      market_value_data?: Array<{
        trim: string;
        'market value': Array<{
          Condition: string;
          'Trade-In': string;
          'Private Party': string;
          'Dealer Retail': string;
        }>;
      }>;
    };
  };
  error?: string;
}

// Title Check - actual response structure
export interface TitleCheckData {
  status: string;
  vin?: string;
  data?: {
    salvage?: boolean;
    salvage_details?: Array<{
      cause?: string;
      date?: string;
    }>;
    flood?: boolean;
    lemon?: boolean;
    odometer_problems?: boolean;
    title_status?: string;
    title_state?: string;
  };
  error?: string;
}

// Auction History - actual response structure (full API response)
export interface AuctionRecord {
  sale_index?: string;
  price?: string;
  'sale status'?: string;
  vname?: string;
  'lot-number'?: string;
  year?: string;
  make?: string;
  model?: string;
  vin?: string;
  'car-features'?: {
    Has_Keys?: string;
    Runs_Drives?: string;
    Engine_Starts?: string;
  };
  'title-and-condition'?: {
    'Title Type'?: string;
    'Title Description'?: string;
    'Primary Damage'?: string;
    'Secondary Damage'?: string;
    VIN?: string;
    Common?: string;
  };
  'technical-specs'?: {
    Odometer?: string;
    'Estimated Repair Cost'?: string;
    'Avg. Estimated Retail Value'?: string;
    'Damage Ratio'?: string;
    'Estimated Winning Bid'?: string;
    'Body Style'?: string;
    Color?: string;
    'Engine Type'?: string;
    'Fuel Type'?: string;
    Cylinders?: string;
    Transmission?: string;
    Drive?: string;
  };
  'sale-date-location'?: {
    'Seller Type'?: string;
    'Auction Date'?: string;
    'Auction Type'?: string;
    Location?: string;
    'Buyer Country'?: string;
  };
  'listing-history'?: Record<string, string>;
  'market-value'?: Record<string, unknown>;
  images?: string[];
}

export interface AuctionData {
  status: string;
  data?: AuctionRecord[];
  error?: string;
}

// Warranty - actual response structure
export interface WarrantyData {
  status: string;
  data?: {
    data?: {
      warranty?: string[];
    };
  };
  error?: string;
}

// Maintenance Schedule - actual response structure
export interface MaintenanceData {
  status: string;
  data?: {
    maintenance?: Array<{
      mileage?: {
        miles?: number;
        km?: number;
      };
      service_items?: string[];
    }>;
  };
  error?: string;
}

// Common Repairs - actual response structure
export interface RepairCost {
  name: string;
  average: number;
  high: number;
  low: number;
}

export interface RepairItem {
  title: string;
  description?: string;
  costs?: {
    independent?: RepairCost[];
    dealer?: RepairCost[];
  };
}

export interface CommonRepairsData {
  status: string;
  data?: {
    repair?: RepairItem[];
  };
  error?: string;
}

// Sales History - actual response structure
export interface SalesRecord {
  sale_date?: string;
  price?: string;
  location?: string;
  condition?: string;
  odometer?: string;
  images?: string[];
}

export interface SalesHistoryData {
  status: string;
  data?: SalesRecord[];
  error?: string;
}
